import React, { useEffect, useState, useRef } from "react"
import { useSelector } from "react-redux"
import { makeStyles } from "@material-ui/core"
import { useMediaQuery } from "react-responsive"

import Snackbar from "@mui/material/Snackbar"
import Button from "@mui/material/Button"
import CustomTableContainer from "../Common/Table/CustomTableContainer"
import BulkUpdatesToolbar from "./BulkUpdatesToolbar"
import InsightView from "./InsightView"
import ButtonFilters from "../Common/Table/ButtonFilters"
import BasicModalNoButton from "../Common/Modals/BasicModalNoButton"
import MobileInsight from "./MobileInsight"
import { ActionsRanking } from "../../util/constants"
import { FormControlLabel, Switch, Grid } from "@mui/material"
import VisibilityIcon from "@mui/icons-material/Visibility"
import ArchiveIcon from "@mui/icons-material/Archive"
import StarIcon from "@mui/icons-material/Star"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Alert from "@mui/material/Alert"
import { FilterManager } from "../../util/filters/filterManager"
import { FILTER_TYPE } from "../../util/filters/filterConstants"

const useStyles = makeStyles({
  cellIcons: {
    display: "flex",
    alignItems: "center",
    width: "100%",
  },
  iconInactive: {
    color: "#6A6A6A",
  },
  iconActive: {
    color: "#3893BE",
  },
})

const initialStatuses = (rowData) => {
  const statuses = []

  rowData.forEach((row) => {
    statuses[row.id] = row.insight.insight_status
  })

  return statuses
}

export default function InsightsTable() {
  const [processedInsights, setProcessedInsights] = useState([])
  const [rowData, setRowData] = useState([])
  const userInsightsData = useSelector((state) => state.userInsights.userInsightsData)
  const deviceData = useSelector((state) => state.device.deviceData)
  const groupData = useSelector((state) => state.group.groupData)
  const [modalData, setModalData] = useState({ open: false })
  const [groupMap, setGroupMap] = useState(null)
  const [deviceMap, setDeviceMap] = useState(null)
  const [showImages, setShowImages] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const actionsIndex = useRef(0)
  const [bulkUpdate, setBulkUpdate] = useState(
    localStorage.getItem("bulkUpdate") ? JSON.parse(localStorage.getItem("bulkUpdate")) : [],
  )
  const [actions, setActions] = useState(
    localStorage.getItem("actions") ? JSON.parse(localStorage.getItem("actions")) : [],
  )
  const [idMap, setIdMap] = useState({})
  const [selectAll, setSelectAll] = useState(false)
  const [selected, setSelected] = useState([])
  const [bulkUpdateHistory, setBulkUpdateHistory] = useState([bulkUpdate])
  const [actionsHistory, setActionsHistory] = useState([])
  const [open, setOpen] = useState(false)

  const classes = useStyles()
  const isMobile = useMediaQuery({ query: "(max-width: 625px)" })

  const popperProps = {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, -15],
        },
      },
    ],
  }

  const filterManagerRef = useRef(null)
  if (!filterManagerRef.current) {
    filterManagerRef.current = FilterManager.Builder.addFilter(FILTER_TYPE.INSIGHT.CLASS_NAME, true)
      .addFilter(FILTER_TYPE.INSIGHT.ALL_CLASS_NAMES, false)
      .addFilter(FILTER_TYPE.INSIGHT.ALG_ID, false)
      .addFilter(FILTER_TYPE.DEVICE.ID, false)
      .addFilter(FILTER_TYPE.DEVICE.NAME, true)
      .addFilter(FILTER_TYPE.DEVICE.LOCATION_NAME, false)
      .addFilter(FILTER_TYPE.INSIGHT.CONFIDENCE, true)
      .addFilter(FILTER_TYPE.INSIGHT.TIMESTAMP, true)
      .addFilter(FILTER_TYPE.INSIGHT.FILE_CTIME, true)
      .addFilter(FILTER_TYPE.INSIGHT.COMMS_SOURCE, true)
      .addFilter(FILTER_TYPE.DEVICE.LATITUDE, true)
      .addFilter(FILTER_TYPE.DEVICE.LONGITUDE, true)
      .addFilter(FILTER_TYPE.DEVICE.MAP_BUTTON, false)
      .addFilter(FILTER_TYPE.INSIGHT.IMAGE, false)
      .addFilter(FILTER_TYPE.DEVICE.GROUPS, false)
      .addFilter(FILTER_TYPE.INSIGHT.STATUS, true)
      .setCompareWith(true)
      .build()
  }
  const filterManager = filterManagerRef.current

  function UnsavedChangesSnackbar(props) {
    const { bulkUpdate, clearBulkUpdate } = props
    return (
      <Snackbar
        open={bulkUpdate.length > 0}
        message={
          bulkUpdate.length === 1
            ? `${bulkUpdate.length} unsaved action`
            : `${bulkUpdate.length} unsaved actions`
        }
        action={
          <Button onClick={clearBulkUpdate} color="inherit" size="small">
            Cancel
          </Button>
        }
      />
    )
  }

  function ActionsButtons(props) {
    const { row } = props

    return (
      <Box className={classes.cellIcons}>
        <Tooltip title="Acknowledge" PopperProps={popperProps}>
          <IconButton onClick={(event) => handleActionClick(event, [row.id], "acknowledge")}>
            <VisibilityIcon
              className={
                actionActive(row.id) === "acknowledge" ? classes.iconActive : classes.iconInactive
              }
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Archive" PopperProps={popperProps}>
          <IconButton onClick={(event) => handleActionClick(event, [row.id], "archive")}>
            <ArchiveIcon
              className={
                actionActive(row.id) === "archive" ? classes.iconActive : classes.iconInactive
              }
            />
          </IconButton>
        </Tooltip>
        <Tooltip title="Favorite" PopperProps={popperProps}>
          <IconButton onClick={(event) => handleActionClick(event, [row.id], "favorite")}>
            <StarIcon
              className={
                actionActive(row.id) === "favorite" ? classes.iconActive : classes.iconInactive
              }
            />
          </IconButton>
        </Tooltip>
      </Box>
    )
  }

  const handleImageToggle = (_) => {
    filterManager.getFilter(FILTER_TYPE.INSIGHT.IMAGE).visibility = !showImages
    setShowImages(!showImages)
  }

  const handleArchivedToggle = (_) => {
    setShowArchived(!showArchived)
  }

  const handleClick = async (_, row) => {
    let newModalData = {}
    newModalData = {
      open: true,
      title: `Class Name: ${row.insight?.class_name}`,
      content: <InsightView row={row} insight={row.insight} ActionsButtons={ActionsButtons} />,
    }
    setModalData(newModalData)
  }

  const getMobileElement = (row) => {
    return <MobileInsight insight={row.insight} />
  }

  const handleClose = () => {
    setModalData({ open: false })
  }

  // bulk updates

  function updateBulkUpdate(ids, action) {
    let updatedBulkUpdate = [...bulkUpdate]
    let currentUpdates = bulkUpdate.length
    let i = 0
    let updated = false

    for (i; i < ids.length; i++) {
      const id = ids[i]
      const currentInsight = idMap[id]
      if (!currentInsight) return
      const filteredBulkUpdate = updatedBulkUpdate.filter((update) => update.id !== id)

      const insightList = currentInsight.collection ? currentInsight.insights : [currentInsight]

      for (const insight of insightList) {
        // cases where bulk update can be updated
        if (
          insight.insight_status !== action &&
          (currentUpdates < 1000 || filteredBulkUpdate.length < updatedBulkUpdate.length)
        ) {
          // case: insight has changed and we have less than 1000 updates or the update is already in the bulk update
          if (filteredBulkUpdate.length === updatedBulkUpdate.length) {
            currentUpdates += 1
          }

          updatedBulkUpdate = filteredBulkUpdate
          updatedBulkUpdate.push({
            id: id,
            insightId: insight.insight_id,
            deviceId: insight.device_id,
            insightStatus: action,
          })
          updated = true
        } else if (insight.insight_status === action) {
          // case: insight has not changed
          updatedBulkUpdate = filteredBulkUpdate
          currentUpdates -= 1
          updated = true
        }
      }

      if (currentUpdates >= 1000) {
        setOpen(true)
        break
      }
    }

    // increment i if there was an update
    if (updated) i += 1

    return [updatedBulkUpdate, i]
  }

  const handleActionClick = (event, ids, action) => {
    event.stopPropagation()
    const updatedActions = [...actions]
    const newAction = updatedActions[ids[0]] === action ? null : action
    const result = updateBulkUpdate(ids, newAction)
    const updatedBulkUpdate = result[0]
    const lastIndex = result[1]

    for (let i = 0; i < lastIndex; i++) {
      const id = ids[i]

      if (updatedActions[id] === action) {
        updatedActions[id] = null
      } else {
        updatedActions[id] = action
      }
    }

    // update history
    if (lastIndex > 0) {
      actionsIndex.current += 1
      setActionsHistory([...actionsHistory.slice(0, actionsIndex.current), updatedActions])
      setBulkUpdateHistory([...bulkUpdateHistory.slice(0, actionsIndex.current), updatedBulkUpdate])
    }

    setActions(updatedActions)
    setBulkUpdate(updatedBulkUpdate)
  }

  const handleUndoClick = () => {
    if (actionsIndex.current > 0) {
      actionsIndex.current = actionsIndex.current - 1
      setActions(actionsHistory[actionsIndex.current])
      setBulkUpdate(bulkUpdateHistory[actionsIndex.current])
    }
  }

  const handloRedoClick = () => {
    if (actionsIndex.current < actionsHistory.length - 1) {
      actionsIndex.current = actionsIndex.current + 1
      setActions(actionsHistory[actionsIndex.current])
      setBulkUpdate(bulkUpdateHistory[actionsIndex.current])
    }
  }

  const clearBulkUpdate = () => {
    actionsIndex.current += 1
    setActionsHistory([...actionsHistory.slice(0, actionsIndex.current), []])
    setBulkUpdateHistory([...bulkUpdateHistory.slice(0, actionsIndex.current), []])

    setSelected([])
    setSelectAll(false)
    setBulkUpdate([])
    setActions(initialStatuses(rowData))
    localStorage.removeItem("bulkUpdate")
    localStorage.removeItem("actions")
  }

  const handleAlertClose = (_) => {
    setOpen(false)
  }

  const canUndo = actionsIndex.current > 0
  const canRedo = actionsIndex.current < actionsHistory.length - 1

  const isSelected = (id) => selected.indexOf(id) !== -1
  const actionActive = (id) => actions[id]

  useEffect(() => {
    setRowData(processedInsights)
  }, [processedInsights])

  useEffect(() => {
    if (groupData) {
      const groupDevice = {}
      for (const group of groupData) {
        for (const device of group.devices) {
          if (!(device in groupDevice)) {
            groupDevice[device] = []
          }
          groupDevice[device].push(group.group_name)
        }
      }
      setGroupMap(groupDevice)
    }
  }, [groupData])

  useEffect(() => {
    if (deviceData) {
      const newDeviceMap = {}
      for (const device of deviceData) {
        newDeviceMap[device.device_id] = device
      }
      setDeviceMap(newDeviceMap)
    }
  }, [deviceData])

  useEffect(() => {
    if (userInsightsData && deviceMap && groupMap) {
      setProcessedInsights(
        userInsightsData.map((insight, index) => {
          return {
            ...filterManager.generateDataMap(
              deviceMap[insight.device_id],
              groupMap[insight.device_id],
              insight,
            ),
            id: index,
            insight: insight,
          }
        }),
      )
    }
  }, [groupMap, userInsightsData, deviceMap, filterManager.generateDataMap])

  // bulk updates
  useEffect(() => {
    const idMap = {}

    rowData.forEach((row) => {
      if (!row.insight) return
      idMap[row.id] = {
        insight_id: row.insight.insight_id,
        device_id: row.device_id,
        insight_status: row.insight.insight_status,
        insights: row.insight.insights,
        collection: row.insight.collection,
      }
    })
    setIdMap(idMap)
  }, [rowData])

  useEffect(() => {
    if (
      localStorage.getItem("bulkUpdate") !== null &&
      JSON.parse(localStorage.getItem("bulkUpdate")).length === 0
    ) {
      localStorage.setItem("actions", JSON.stringify(initialStatuses(rowData)))
    }
    setActions(JSON.parse(localStorage.getItem("actions")))
  }, [rowData])

  useEffect(() => {
    localStorage.setItem("bulkUpdate", JSON.stringify(bulkUpdate))
  }, [bulkUpdate])

  return (
    <>
      {!isMobile ? (
        <Grid container justifyContent="flex-end">
          <FormControlLabel
            checked={showImages}
            onChange={handleImageToggle}
            control={<Switch />}
            label="Show images"
          />
          <FormControlLabel
            checked={showArchived}
            onChange={handleArchivedToggle}
            control={<Switch />}
            label="Show archived"
          />
        </Grid>
      ) : null}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleAlertClose} severity={"info"} variant="filled" sx={{ width: "100%" }}>
          You've hit the 1000 insight update limit. Please save your current changes and start
          another update.
        </Alert>
      </Snackbar>
      <CustomTableContainer
        defaultOrder="desc"
        defaultOrderHeader={FILTER_TYPE.INSIGHT.CLASS_NAME}
        headers={filterManager.getHeaders()}
        filterManager={filterManager}
        rowData={rowData}
        paginationOptions={[5, 10, 25, 50, 100]}
        defaultPaginationOption={25}
        clickAction={handleClick}
        getMobileElement={getMobileElement}
        swipeable={false}
        showArchived={showArchived}
        checkBoxes={true}
        handleArchivedToggle={handleArchivedToggle}
        AdditionalToolbar={
          <BulkUpdatesToolbar
            bulkUpdate={bulkUpdate}
            setBulkUpdate={setBulkUpdate}
            setSelectedRows={setSelected}
            selectedRows={selected}
            onActionClick={handleActionClick}
            actions={actions}
            canUndo={canUndo}
            canRedo={canRedo}
            onUndoClick={handleUndoClick}
            onRedoClick={handloRedoClick}
            setSelectAll={setSelectAll}
          />
        }
        handleActionClick={handleActionClick}
        handleUndoClick={handleUndoClick}
        handloRedoClick={handloRedoClick}
        canUndo={canUndo}
        canRedo={canRedo}
        actions={actions}
        bulkUpdate={bulkUpdate}
        setBulkUpdate={setBulkUpdate}
        actionsHistory={actionsHistory}
        setActionsHistory={setActionsHistory}
        initialStatuses={() => initialStatuses(rowData)}
        setSelectAll={setSelectAll}
        selectAll={selectAll}
        setSelected={setSelected}
        selected={selected}
        isSelected={isSelected}
        actionActive={actionActive}
        UnsavedChangesSnackbar={
          <UnsavedChangesSnackbar bulkUpdate={bulkUpdate} clearBulkUpdate={clearBulkUpdate} />
        }
        ActionsButtons={ActionsButtons}
        ButtonFilters={ButtonFilters}
        buttonRanking={ActionsRanking}
      />
      <BasicModalNoButton
        fullWidth
        title={modalData.title}
        content={modalData.content}
        confirmText="Close"
        noCancel
        confirmAction={handleClose}
        passedOpen={modalData.open}
      />
    </>
  )
}
